import { Component, ErrorInfo, ReactNode } from 'react'
import { logger } from '@/utils/logger'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  // Bumped each time the URL or `online` state changes so we can
  // forget a previously-thrown error and retry rendering. Without
  // this, a transient crash (e.g. a backend pod replace that
  // produced one bad response while React was rendering) sticks
  // until the user manually clicks Refresh — even though
  // navigating to any other route would have rendered fine.
  resetKey: number
}

export class ErrorBoundary extends Component<Props, State> {
  private routeChangeListener?: () => void
  private onlineListener?: () => void
  private lastUrl: string = typeof window !== 'undefined' ? window.location.href : ''

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, resetKey: 0 }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale-chunk recovery: when React's Suspense catches a failed
    // lazy() dynamic import (post-deploy with old chunk hashes), reload
    // the page once instead of showing the generic error fallback.
    // main.tsx exposes the same gated reload logic used for unhandled
    // promise rejections — single source of truth for the regex + cooldown.
    const reloader = (window as any).__reloadOnStaleChunk
    if (typeof reloader === 'function') {
      reloader(error?.message || '')
    }
    logger.error('React Error Boundary caught an error', error, {
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    })
  }

  componentDidMount() {
    // Auto-reset on network restoration. WebSocket / fetch hiccups
    // during a backend deploy can crash render mid-flight; once
    // the network is back the page is usually fine to re-render.
    this.onlineListener = () => {
      if (this.state.hasError) {
        logger.info('Network back online — clearing error boundary')
        this.setState((s) => ({ hasError: false, error: undefined, resetKey: s.resetKey + 1 }))
      }
    }
    window.addEventListener('online', this.onlineListener)

    // Auto-reset on route change. The boundary watches the URL via
    // a polling pattern (popstate + click + history mutation are
    // all intercepted by react-router and don't fire window-level
    // events reliably across versions). A 1 s poll is cheap enough
    // and only does work when the URL actually changes; the
    // resetKey bump triggers a re-render of the children tree.
    this.routeChangeListener = () => {
      if (window.location.href !== this.lastUrl) {
        this.lastUrl = window.location.href
        if (this.state.hasError) {
          logger.info('Route change detected — clearing error boundary')
          this.setState((s) => ({ hasError: false, error: undefined, resetKey: s.resetKey + 1 }))
        }
      }
    }
    const intervalId = window.setInterval(this.routeChangeListener, 1000)
    // Stash interval id on the listener function for cleanup so
    // we don't have to track it as a separate field.
    ;(this.routeChangeListener as any).__intervalId = intervalId
  }

  componentWillUnmount() {
    if (this.onlineListener) window.removeEventListener('online', this.onlineListener)
    if (this.routeChangeListener) {
      const id = (this.routeChangeListener as any).__intervalId
      if (id) window.clearInterval(id)
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-neutral-900">
                Something went wrong
              </h3>
              <p className="mt-2 text-sm text-neutral-500">
                We're sorry, but something unexpected happened. Please try
                refreshing the page, or navigating to another section.
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
                <button
                  onClick={() =>
                    this.setState((s) => ({
                      hasError: false,
                      error: undefined,
                      resetKey: s.resetKey + 1,
                    }))
                  }
                  className="inline-flex items-center justify-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Try again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // The resetKey is keyed on the children tree so a reset
    // remounts the subtree, throwing away whatever stale state
    // caused the original crash.
    return <div key={this.state.resetKey}>{this.props.children}</div>
  }
}
